import React from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { SpeedInsights } from '@vercel/speed-insights/react'
import { Analytics as VercelAnalytics } from '@vercel/analytics/react'
import ErrorBoundary from './components/ErrorBoundary'
import Layout from './components/Layout'
import Dashboard from './pages/Dashboard'
import Feed from './pages/Feed'
import Signals from './pages/Signals'
import ContentHub from './pages/ContentHub'
import Scheduled from './pages/Scheduled'
import Analytics from './pages/Analytics'
import Monetization from './pages/Monetization'
import Subscriptions from './pages/Subscriptions'
import ForgotPassword from './pages/ForgotPassword'
import Landing from './pages/Landing'
import Login from './pages/Login'
import Register from './pages/Register'
import ResetPassword from './pages/ResetPassword'
import Profile from './pages/Profile'
import CreatorProfile from './pages/CreatorProfile'
import Creators from './pages/Creators'
import JoinReferral from './pages/JoinReferral'
import PendingReferralRedirect from './components/referrals/PendingReferralRedirect'
import TrackRecord from './pages/TrackRecord'
import PaperPositions from './pages/PaperPositions'
import Marketplace from './pages/Marketplace'
import Leaderboard from './pages/Leaderboard'
import PriceAlerts from './pages/PriceAlerts'
import MyCalls from './pages/MyCalls'
import LockCall from './pages/LockCall'
import ABTesting from './pages/ABTesting'
import LiveRoom from './pages/LiveRoom'
import Settings from './pages/Settings'
import EngineSettings from './pages/EngineSettings'
import Trades from './pages/Trades'
import Exchange from './pages/Exchange'
import Practice from './pages/Practice'
import AdminVetting from './pages/AdminVetting'
import AdminExitBacktest from './pages/AdminExitBacktest'
import AdminDiagnostics from './pages/AdminDiagnostics'
import AdminReports from './pages/AdminReports'
import Terms from './pages/Terms'
import Privacy from './pages/Privacy'
import FAQ from './pages/FAQ'
import PublicSignal from './pages/PublicSignal'
import CreatorRecord from './pages/CreatorRecord'
import Verify from './pages/Verify'
import FeeCalculator from './pages/FeeCalculator'
import VerifyEmail from './pages/VerifyEmail'
import CoursePage from './pages/CoursePage'
import LessonPage from './pages/LessonPage'
import CourseStudio from './pages/CourseStudio'
import CourseEditor from './pages/CourseEditor'
import CertificatePage from './pages/CertificatePage'
import CourseCatalog from './pages/CourseCatalog'
import { CATALOG_PATH, CERTIFICATE_PATH, COURSE_PATH, LESSON_PATH, STUDIO_PATH } from './components/courses/courseUtils'
import { useAuthStore } from './store/authStore'
import useWhiteLabelBootstrap from './hooks/useWhiteLabelBootstrap'
import {
  CALL_PATH,
  CREATOR_RECORD_PATH,
  FEE_CALCULATOR_PATH,
  LOCK_CALL_PATH,
  MY_CALLS_PATH,
  PAPER_POSITIONS_PATH,
  VERIFY_PATH,
} from './routes'
import LoadingScreen from './components/LoadingScreen'

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore()
  if (!user?.is_superuser) return <Navigate to="/" replace />
  return <>{children}</>
}

/** Signed out, "/" is the public landing page; every other app path goes to /login. */
function SignedOut() {
  const { pathname } = useLocation()
  return pathname === '/' ? <Landing /> : <Navigate to="/login" replace />
}

function App() {
  const { token } = useAuthStore()
  const whiteLabel = useWhiteLabelBootstrap()

  if (whiteLabel.status === 'checking') {
    return <LoadingScreen />
  }

  if (whiteLabel.status === 'storefront' && whiteLabel.creatorId) {
    return (
      <ErrorBoundary>
        <Router>
          <Toaster position="top-right" />
          <Routes>
            <Route path="/creator/:id" element={<CreatorProfile />} />
            <Route path="*" element={<Navigate to={`/creator/${whiteLabel.creatorId}`} replace />} />
          </Routes>
        </Router>
      </ErrorBoundary>
    )
  }

  // The signed-in app: "/" is the Dashboard.
  const signedInApp = (
    <Layout>
      <PendingReferralRedirect />
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/content" element={<ContentHub tab="library" />} />
        <Route path="/content/automation" element={<ContentHub tab="automation" />} />
        <Route path="/content/clips" element={<ContentHub tab="clips" />} />
        <Route path="/content/newsletter" element={<ContentHub tab="newsletter" />} />
        <Route path="/content/notes" element={<ContentHub tab="notes" />} />
        {/* Kept so existing links still land on the Create tab. */}
        <Route path="/create" element={<ContentHub tab="create" />} />
        <Route path="/scheduled" element={<Scheduled />} />
        <Route path="/feed" element={<Feed />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/signals" element={<Signals />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/settings/engine" element={<AdminRoute><EngineSettings /></AdminRoute>} />
        <Route path="/trades" element={<AdminRoute><Trades /></AdminRoute>} />
        <Route path="/exchange" element={<AdminRoute><Exchange /></AdminRoute>} />
        <Route path="/practice" element={<AdminRoute><Practice /></AdminRoute>} />
        <Route path="/admin/vetting" element={<AdminRoute><AdminVetting /></AdminRoute>} />
        <Route path="/admin/exit-backtest" element={<AdminRoute><AdminExitBacktest /></AdminRoute>} />
        <Route path="/admin/diagnostics" element={<AdminRoute><AdminDiagnostics /></AdminRoute>} />
        <Route path="/admin/reports" element={<AdminRoute><AdminReports /></AdminRoute>} />
        <Route path="/monetization" element={<Monetization />} />
        <Route path="/subscriptions" element={<Subscriptions />} />
        <Route path="/creator/:id" element={<CreatorProfile />} />
        <Route path="/creators" element={<Creators />} />
        <Route path="/track-record" element={<TrackRecord />} />
        <Route path={PAPER_POSITIONS_PATH} element={<PaperPositions />} />
        <Route path="/marketplace" element={<Marketplace />} />
        <Route path={STUDIO_PATH} element={<CourseStudio />} />
        <Route path="/courses/:id" element={<CourseEditor />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/alerts" element={<PriceAlerts />} />
        {/* Lock a call: each page redirects home unless the server says it's on for this user. */}
        <Route path={MY_CALLS_PATH} element={<MyCalls />} />
        <Route path={LOCK_CALL_PATH} element={<LockCall />} />
        <Route path="/ab-testing" element={<ABTesting />} />
        <Route path="/live-room" element={<LiveRoom />} />
        <Route path="/live-room/:sessionId" element={<LiveRoom />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  )

  return (
    <ErrorBoundary>
    <Router>
      {/* app-toast: themed dark by theme/terminal-theme.css on signed-in pages */}
      <Toaster position="top-right" toastOptions={{ className: 'app-toast' }} />
      <SpeedInsights />
      <VercelAnalytics />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/faq" element={<FAQ />} />
        {/* Public record pages: no login, outside the signed-in Layout. */}
        <Route path={CALL_PATH} element={<PublicSignal />} />
        <Route path={CREATOR_RECORD_PATH} element={<CreatorRecord />} />
        <Route path={CATALOG_PATH} element={<CourseCatalog />} />
        <Route path={CERTIFICATE_PATH} element={<CertificatePage />} />
        <Route path={COURSE_PATH} element={<CoursePage />} />
        <Route path={LESSON_PATH} element={<LessonPage />} />
        <Route path={VERIFY_PATH} element={<Verify />} />
        <Route path={FEE_CALCULATOR_PATH} element={<FeeCalculator />} />
        {/* A subscriber's share link: works signed in or out. */}
        <Route path="/join/:code" element={<JoinReferral />} />
        <Route path="/*" element={token ? signedInApp : <SignedOut />} />
      </Routes>
    </Router>
    </ErrorBoundary>
  )
}

export default App
